import { z } from "zod"

import { useQueryAPI } from "@/hooks/query-api"

// Schema based on the provided API response structure
const QueueDataSchema = z.array(z.record(z.string(), z.array(z.string())))

const ListQueuesResponseSchema = z.object({
  data: QueueDataSchema,
})

export type CampaignNameToPhoneNumberMap = Record<string, string>
export type PhoneNumberToCampaignCountMap = Record<string, number>

/**
 * Fetches the list of queues and associated phone numbers.
 * Returns the raw data, a map of campaign names to phone numbers,
 * and a map of phone numbers to their associated campaign count.
 */
export function useFetchQueues() {
  const queryResult = useQueryAPI({
    apiEndPoint: "/inbound-calls/list-queues",
    schema: ListQueuesResponseSchema,
    // Stale time can be adjusted based on how often this data changes
    options: {
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  })

  // Process the data into maps for easier lookup
  const campaignNameToPhoneNumberMap: CampaignNameToPhoneNumberMap = {}
  const phoneNumberToCampaignCountMap: PhoneNumberToCampaignCountMap = {}

  if (queryResult.data?.data) {
    for (const phoneEntry of queryResult.data.data) {
      for (const phoneNumber in phoneEntry) {
        const campaignNames = phoneEntry[phoneNumber]
        // Increment count for this phone number by the number of campaigns associated with it
        phoneNumberToCampaignCountMap[phoneNumber] =
          (phoneNumberToCampaignCountMap[phoneNumber] || 0) +
          campaignNames.length

        for (const campaignName of campaignNames) {
          // Handle potential duplicate campaign names across different numbers (take the first found)
          if (!(campaignName in campaignNameToPhoneNumberMap)) {
            campaignNameToPhoneNumberMap[campaignName] = phoneNumber
          }
        }
      }
    }
  }

  return {
    ...queryResult,
    campaignNameToPhoneNumberMap,
    phoneNumberToCampaignCountMap,
  }
}
